import React from "react";
import { Link } from "react-router-dom";
import moment from "moment-timezone";
import Tooltip from "@mui/material/Tooltip";

import currencyFormatter from "../../helpers/currencyFormatter";
import shortenName from "../../helpers/shorten-name";
import { formatTripDate, tripDateSortKey } from "../../helpers/dates";

const columns = [
  {
    name: "View",
    sortable: false,
    cell: (row) => {
      return <Link to={`/trips/${row._id}`} target="_blank" rel="noopener noreferrer">View</Link>;
    },
    width: "70px",
  },
  {
    name: "Created At",
    selector: "createdAt",
    sortable: true,
    width: "130px",
    cell: (row) => {
      return moment(row.createdAt).format("M/D/YY - HHmm");
    },
  },

  {
    name: "Created By",
    selector: "createdBy.name",
    sortable: true,
    width: "150px",
  },
  {
    name: "Trip #",
    selector: "tripTag",
    sortable: true,
    width: "110px",
    cell: (row) => {
      return (
        <Link className="ellipsis-cell" to={`/trips/${row._id}`} target="_blank" rel="noopener noreferrer">
          <Tooltip title={row.tripTag} arrow>
            {row.tripTag}
          </Tooltip>
        </Link>
      );
    },
  },
  {
    name: "Client Name",
    selector: "clientName",
    sortable: true,
    width: "150px",
    cell: (row) => {
      return row.displayClientName;
    },
  },

  {
    name: "Operator",
    selector: "operatorName",
    sortable: true,
    width: "110px",
  },
  {
    name: "Departure Date",
    selector: "departureDate", // Keep this simple for DataTable's internal use
    sortable: true,
    width: "110px",
    cell: (row) => {
      const departureDate = row?.itineraryData?.[0]?.legs?.[0]?.departureDate;
      return departureDate ? formatTripDate(departureDate) : "-";
    },
    sortFunction: (a, b) => {
      // This is just for client-side fallback - server-side sort will take precedence
      // Compare calendar days, not instants, so 00:00Z and 04:00Z legs on the
      // same day do not interleave. Undated rows sink rather than jumping to the
      // top, which an empty sort key would do.
      const dateA =
        tripDateSortKey(a?.itineraryData?.[0]?.legs?.[0]?.departureDate) ||
        "9999-12-31";
      const dateB =
        tripDateSortKey(b?.itineraryData?.[0]?.legs?.[0]?.departureDate) ||
        "9999-12-31";
      return dateA.localeCompare(dateB);
    },
  },

  {
    name: "Return Date",
    selector: "returnDate",
    sortable: true,
    width: "110px",
    cell: (row) => {
      if (row.returnDate) {
        return formatTripDate(row.returnDate);
      }

      return "-";
    },
  },

  {
    name: "Contracts",
    selector: "contractListString",
    sortable: false,
    width: "150px",
  },

  {
    name: "OFRs",
    selector: "ofrListString",
    sortable: false,
    width: "150px",
  },

  {
    name: "Routing",
    selector: "latestRoutingString",
    sortable: false,
    width: "200px",
  },

  {
    name: "Next Penalty",
    selector: "nextPenalty.dueDate",
    sortable: true,
    width: "150px",
    cell: (row) => {
      return row.nextPenalty?.dueDate
        ? moment(row.nextPenalty.dueDate).format("M/D/YY")
        : "-";
    },
  },

  {
    name: "Stage",
    selector: "displayStage",
    sortable: true,
    width: "130px",
    cell: (row) => {
      switch (row.displayStage) {
        case "newTrip":
          return "Active";
        case "ended":
          return "Ended";
        case "canceled":
          return "Canceled";
        case "archived":
          return "Archived";
        case "closedPassthrough":
          return "Closed - Passthrough";
        case "inReview":
          return "In Review";
        case "inProgress":
          return "Active";
        case "pending":
          return "Pending";
        default:
          return "Unknown";
      }
    },
  },
];

const opsColumns = [
  {
    name: "Lock",
    selector: "isLocked",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "70px",
  },
  {
    name: "Stage",
    selector: "displayStage",
    sortable: true,
    width: "130px",
    cell: (row) => {
      switch (row.displayStage) {
        case "newTrip":
          return "Active";
        case "ended":
          return "Ended";
        case "canceled":
          return "Canceled";
        case "archived":
          return "Archived";
        case "closedPassthrough":
          return "Closed - Passthrough";
        case "inReview":
          return "In Review";
        case "inProgress":
          return "Active";
        case "pending":
          return "Pending";
        default:
          return "Unknown";
      }
    },
  },
  // Trip Data
  {
    name: "Owner",
    selector: "ownerName",
    sortable: true,
    width: "150px",
    cell: (row) => {
      return shortenName(row.owner?.name);
    },
  },
  {
    name: "Trip #",
    selector: "tripTag",
    sortable: true,
    width: "110px",
    cell: (row) => {
      return (
        <Link className="ellipsis-cell" to={`/trips/${row._id}`} target="_blank" rel="noopener noreferrer">
          <Tooltip title={row.tripTag} arrow>
            {row.tripTag}
          </Tooltip>
        </Link>
      );
    },
  },
  {
    name: "Created",
    selector: "createdAt",
    sortable: true,
    width: "130px",
    cell: (row) => {
      return moment(row.createdAt).format("M/D/YY");
    },
  },
  {
    name: "Client",
    selector: "clientName",
    sortable: true,
    width: "150px",
    cell: (row) => {
      return row.displayClientName;
    },
  },
  {
    name: "First Leg",
    selector: "firstLeg",
    cell: (row) => {
      if (!row.firstLeg) {
        return "N/A";
      }

      return `${formatTripDate(row.firstLeg.departureDate)} ${row.firstLeg.departureAirport?.code || ""}-${row.firstLeg.arrivalAirport?.code || ""}`;
    },
    sortable: true,
    width: "160px",
  },
  {
    name: "Last Leg",
    selector: "lastLeg",
    cell: (row) => {
      if (!row.lastLeg) {
        return "N/A";
      }

      return `${formatTripDate(row.lastLeg.departureDate)} ${row.lastLeg.departureAirport?.code || ""}-${row.lastLeg.arrivalAirport?.code || ""}`;
    },
    sortable: true,
    width: "160px",
  },
  {
    name: "Next Leg",
    selector: "nextLeg",
    cell: (row) => {
      if (!row.nextLeg) {
        return "N/A";
      }

      return `${formatTripDate(row.nextLeg.departureDate)} ${row.nextLeg.departureAirport?.code || ""}-${row.nextLeg.arrivalAirport?.code || ""}`;
    },
    sortable: true,
    width: "160px",
  },
  {
    name: "Operator",
    selector: "operatorName",
    sortable: true,
    width: "110px",
    cell: (row) => {
      if (!row.operatorFinancialRecords?.length) {
        return "N/A";
      }

      const signedOfrOperators = [...new Set(row.operatorFinancialRecords.filter(({ stage }) => stage === "signed").map(({ operator }) => operator?.name).filter(Boolean))].sort(((a, b) => a?.localeCompare(b || '')));
      const savedOfrOperators = [...new Set(row.operatorFinancialRecords.filter(({ stage }) => stage === "saved").map(({ operator }) => operator?.name).filter(Boolean))].sort(((a, b) => a?.localeCompare(b || '')));

      const operators = [...signedOfrOperators];
      savedOfrOperators.forEach((operator) => {
        if (!operators.includes(operator)) {
          operators.push(operator);
        }
      })

      return (
        <Tooltip title={operators.join(", ") || ""}>
          <div className="two-line-content">
            {operators.join(", ") || ""}
          </div>
        </Tooltip>
      );
    },
  },
  // Financials
  {
    name: "Evo Contracts",
    selector: "signedContractsFlightCharges",
    sortable: true,
    width: "110px",
    cell: (row) => {
      if (
        row?.signedContractsFlightCharges &&
        !isNaN(row?.signedContractsFlightCharges)
      ) {
        return currencyFormatter.format(row.signedContractsFlightCharges);
      }

      return "N/A";
    },
  },
  {
    name: "Client Balance",
    selector: "clientBalance",
    sortable: true,
    width: "110px",
    cell: (row) => {
      if (
        (row?.clientBalance || row.clientBalance === 0) &&
        !isNaN(row?.clientBalance)
      ) {
        return <div
          className={`amount-${row.clientBalance === 0 ? "positive" : "negative"}`}>{currencyFormatter.format(row.clientBalance)}</div>;
      }

      return "N/A";
    },
  },
  {
    name: "OFRs",
    selector: "signedOFRsFlightCharges",
    sortable: true,
    width: "110px",
    cell: (row) => {
      if (
        row?.signedOFRsFlightCharges &&
        !isNaN(row?.signedOFRsFlightCharges)
      ) {
        return currencyFormatter.format(row.signedOFRsFlightCharges);
      }

      return "N/A";
    },
  },
  {
    name: "OFR Balance",
    selector: "ofrBalance",
    sortable: true,
    width: "110px",
    cell: (row) => {
      if (
        row?.signedOFRsFlightCharges &&
        !isNaN(row?.signedOFRsFlightCharges) &&
        (row?.ofrBalance || row?.ofrBalance === 0) &&
        !isNaN(row?.ofrBalance)
      ) {
        return <div
          className={`amount-${row.ofrBalance === 0 ? "positive" : "negative"}`}>{currencyFormatter.format(row.ofrBalance)}</div>;
      }

      return "N/A";
    },
  },
  {
    name: "Next Penalty",
    selector: "nextPenalty",
    sortable: true,
    width: "150px",
    cell: (row) => {
      if (!row.nextPenalty) {
        return "N/A";
      }

      const nextPenalty = `${moment(row.nextPenalty.dueDate).format("M/D/YY")} - ${row.nextPenalty.type === 1 ? `${row.nextPenalty.value}%` : currencyFormatter.format(row.nextPenalty.value)} ${row.nextPenalty.operator?.name || ""} `;
      return (
        <Tooltip
          classes={{
            tooltip: "multiline-tooltip",
          }}
          title={nextPenalty}
        >
          <div className="two-line-content">
            {moment(row.nextPenalty.dueDate).format("M/D/YY")} - {row.nextPenalty.type === 1 ? `${row.nextPenalty.value}%` : currencyFormatter.format(row.nextPenalty.value)}
            <br/>
            {row.nextPenalty.operator?.name || ""}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Client Pay Date",
    selector: "clientPayDate",
    sortable: true,
    width: "150px",
    cell: (row) => {
      if (!row.clientPayDate) {
        return (
          <Link to={`/trips/${row._id}?tab=client-pay-date`} target="_blank" rel="noopener noreferrer">
            <span
              className="material-icons"
              style={{
                color: "red",
              }}
            >
              add
            </span>
          </Link>
        );
      }

      return moment(row.clientPayDate).format("M/D/YY");
    },
  },
  {
    name: "CC?",
    selector: "isFinancialsCC",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "70px",
  },
  // Notes
  {
    name: "Trip Notes",
    selector: "tripNotes",
    sortable: false,
    cell: (row) => {
      if (!row.tripNotes?.length) {
        return "N/A";
      }

      return (
        <Tooltip
          classes={{
            tooltip: "multiline-tooltip",
          }}
          title={row.tripNotes.map((note) => (
            `- ${note.note}`
          )).join("\n")}
        >
          <div className="two-line-content">
            {row.tripNotes.map((note, index) => (
              <div key={index}>&bull; {note.category ? `${note.category}: ` : ""}{note.note}</div>
            ))}
          </div>
        </Tooltip>
      );
    },
    width: "200px",
  },
  {
    name: " ",
    selector: "tripNotesAction",
    sortable: false,
    cell: (row) => {
      return (
        <Link to={`/trips/${row._id}?tab=tripNotes`} target="_blank" rel="noopener noreferrer">
          {row.tripNotes?.length > 0 ? "Edit" : "Add"}
        </Link>
      );
    },
    width: "60px",
  },
  // Ground Transportation
  {
    name: "Vendor",
    selector: "groundTransportationVendor",
    sortable: false,
    cell: (row) => {
      if (!row.groundTransportation?.length) {
        return "N/A";
      }

      const vendors = [...new Set((row.groundTransportation.map((item) => item.vendor?.name).filter(Boolean)))];

      return (
        <Tooltip
          classes={{
            tooltip: "multiline-tooltip",
          }}
          title={vendors.join(", ")}
        >
          <div className="two-line-content">
            {vendors.join(", ")}
          </div>
        </Tooltip>
      );
    },
    width: "200px",
  },
  {
    name: " ",
    selector: "groundTransportationAction",
    sortable: false,
    cell: (row) => {
      return (
        <Link to={`/trips/${row._id}?tab=groundTransportation`} target="_blank" rel="noopener noreferrer">
          {row.groundTransportation?.length > 0 ? "Edit" : "Add"}
        </Link>
      );
    },
    width: "60px",
  },
  {
    name: "Amount",
    selector: "groundTransportationAmount",
    sortable: false,
    cell: (row) => {
      if (!row.groundTransportation?.length) {
        return "N/A";
      }

      return currencyFormatter.format(row.groundTransportation.reduce((acc, item) => acc + item.amount, 0));
    },
    width: "110px",
  },
  {
    name: "24hr Check",
    selector: "isGroundTransportation24hCheck",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "70px",
  },
  // Catering
  {
    name: "Vendor",
    selector: "cateringVendor",
    sortable: false,
    cell: (row) => {
      if (!row.catering?.length) {
        return "N/A";
      }

      const vendors = [...new Set((row.catering.map((item) => item.vendor?.name).filter(Boolean)))];

      return (
        <Tooltip
          classes={{
            tooltip: "multiline-tooltip",
          }}
          title={vendors.join(", ")}
        >
          <div className="two-line-content">
            {vendors.join(", ")}
          </div>
        </Tooltip>
      );
    },
    width: "200px",
  },
  {
    name: " ",
    selector: "cateringAction",
    sortable: false,
    cell: (row) => {
      return (
        <Link to={`/trips/${row._id}?tab=catering`} target="_blank" rel="noopener noreferrer">
          {row.catering?.length > 0 ? "Edit" : "Add"}
        </Link>
      );
    },
    width: "60px",
  },
  {
    name: "Amount",
    selector: "cateringAmount",
    sortable: false,
    cell: (row) => {
      if (!row.catering?.length) {
        return "N/A";
      }

      return currencyFormatter.format(row.catering.reduce((acc, item) => acc + item.amount, 0));
    },
    width: "110px",
  },
  {
    name: "24hr Check",
    selector: "isCatering24hCheck",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "70px",
  },
  // Itinerary
  {
    name: "App User",
    selector: "appUser",
    sortable: false,
    cell: (row) => {
      return (
        <span data-testid="ops-view-app-user">
          {row.appUser ? "Yes" : "No"}
        </span>
      );
    },
    width: "70px",
  },
  {
    name: "View",
    selector: "viewItinerary",
    sortable: false,
    cell: (row) => {
      if (!row.itineraryData?.length) {
        return (
          <Link to={`/trips/${row._id}/itineraries/new`} target="_blank" rel="noopener noreferrer">
            <span
              className="material-icons"
              style={{
                color: "red",
              }}
            >
              add
            </span>
          </Link>
        );
      }

      const { _id: itineraryId } = row.itineraryData[0];

      return (
        <Link to={`/trips/${row._id}/itineraries/${itineraryId}`} target="_blank" rel="noopener noreferrer">
          <span className="material-icons">edit</span>
        </Link>
      );
    },
    width: "60px",
  },
  {
    name: "Prelim",
    selector: "prelim",
    sortable: false,
    cell: (row) => {
      if (!row.itineraryData?.length) {
        return <span data-testid="ops-view-itinerary-prelim">N/A</span>;
      }

      const { actionLog } = row.itineraryData[0];

      if (actionLog) {
        const lastAction = actionLog.filter(({
          action,
          data,
        }) => [
          "sendEmail",
          "syncToApp",
          "sendEmailAndSyncToApp",
        ].includes(action) && data?.template?.includes("preliminary")).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.[0];
        if (lastAction) {
          const actionsMap = {
            "sendEmail": "Email",
            "syncToApp": "Sync",
            "sendEmailAndSyncToApp": "Email & Sync",
          };

          return <span data-testid="ops-view-itinerary-prelim">{moment(lastAction.timestamp).format("M/D/YYYY - HHmm")}<br/>({actionsMap[lastAction.action] || ""})</span>
        }
      }

      return <span data-testid="ops-view-itinerary-prelim">N/A</span>;
    },
    width: "145px",
  },
  {
    name: "Final",
    selector: "final",
    sortable: false,
    cell: (row) => {
      if (!row.itineraryData?.length) {
        return <span data-testid="ops-view-itinerary-final">N/A</span>;
      }

      const { actionLog } = row.itineraryData[0];

      if (actionLog) {
        const lastAction = actionLog.filter(({
          action,
          data,
        }) => [
          "sendEmail",
          "syncToApp",
          "sendEmailAndSyncToApp",
        ].includes(action) && data?.template?.includes("final")).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.[0];
        if (lastAction) {
          const actionsMap = {
            "sendEmail": "Email",
            "syncToApp": "Sync",
            "sendEmailAndSyncToApp": "Email & Sync",
          };

          return <span data-testid="ops-view-itinerary-final">{moment(lastAction.timestamp).format("M/D/YYYY - HHmm")}<br/>({actionsMap[lastAction.action] || ""})</span>
        }
      }

      return <span data-testid="ops-view-itinerary-final">N/A</span>;
    },
    width: "145px",
  },
  {
    name: "Pax",
    selector: "pax",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "150px",
  },
  {
    name: "48hr Weather",
    selector: "is48hrWeather",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "80px",
  },
  {
    name: "Repo",
    selector: "isRepo",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "70px",
  },
  {
    name: "In position",
    selector: "isInPosition",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "80px",
  },
  {
    name: "Day Of",
    selector: "isDayOf",
    sortable: false,
    cell: () => {
      return null;
    },
    width: "70px",
  },
];

export { columns, opsColumns };
